import { api } from './authService';
export { api };

// Rewrite any legacy absolute URLs that were pinned to a specific host
// (e.g. an old Preview domain) so `<img>` resolves them against the current
// origin. Idempotent — non-upload URLs are returned untouched.
export const resolveImageUrl = (u) => {
  if (!u || typeof u !== 'string') return u;
  const i = u.indexOf('/api/uploads/');
  if (i > 0 && /^https?:\/\//.test(u)) return u.slice(i);
  return u;
};

const _fixProductImages = (p) => {
  if (!p || typeof p !== 'object') return p;
  const next = { ...p };
  if (next.image) next.image = resolveImageUrl(next.image);
  if (Array.isArray(next.images)) next.images = next.images.map(resolveImageUrl);
  if (next.variants && typeof next.variants === 'object') {
    const v = { ...next.variants };
    if (Array.isArray(v.colors)) {
      v.colors = v.colors.map((c) => (c && c.image ? { ...c, image: resolveImageUrl(c.image) } : c));
    }
    next.variants = v;
  }
  return next;
};

// Products
export const svcListProducts = () => api.get('/products').then((r) => (Array.isArray(r.data) ? r.data.map(_fixProductImages) : r.data));
export const svcCreateProduct = (body) => api.post('/products', body).then((r) => _fixProductImages(r.data));
export const svcUpdateProduct = (id, patch) => api.patch(`/products/${id}`, patch).then((r) => _fixProductImages(r.data));
export const svcDeleteProduct = (id) => api.delete(`/products/${id}`).then((r) => r.data);
export const svcBulkPrice = (ids, percent) => api.post('/products/bulk-price', { ids, percent }).then((r) => r.data);

// Categories (server-managed)
export const svcListCategories = () => api.get('/categories').then((r) => r.data);
export const svcCreateCategory = (body) => api.post('/categories', body).then((r) => r.data);
export const svcUpdateCategory = (id, patch) => api.patch(`/categories/${id}`, patch).then((r) => r.data);
export const svcDeleteCategory = (id) => api.delete(`/categories/${id}`).then((r) => r.data);

// Stores
export const svcListStores = () => api.get('/stores').then((r) => r.data);
export const svcCreateStore = (body) => api.post('/stores', body).then((r) => r.data);
export const svcUpdateStore = (storeId, patch) => api.patch(`/stores/${storeId}`, patch).then((r) => r.data);
export const svcDeleteStore = (storeId) => api.delete(`/stores/${storeId}`).then((r) => r.data);
export const svcSetStoreAvailability = (storeId, availableProductIds) => api.patch(`/stores/${storeId}/availability`, { available_product_ids: availableProductIds }).then((r) => r.data);

// Uploads
// Backend returns a RELATIVE URL (e.g. `/api/uploads/xxx.webp`). Do not
// prepend REACT_APP_BACKEND_URL here — the value is persisted into MongoDB
// and would break the moment the app is served from a different host
// (Preview vs Production). Relative paths resolve against the current origin
// which shares `/api/*` routing with the backend via the ingress.
//
// iter38 · FIX Network Error en subida de fotos:
// El ingress de K8s inyecta `access-control-allow-origin: *` en las respuestas,
// lo cual es INCOMPATIBLE con `access-control-allow-credentials: true` del
// FastAPI CORSMiddleware. El navegador bloquea la respuesta y axios devuelve
// "Network Error" aunque el archivo sí llegó al servidor. Como la autenticación
// se hace con Authorization: Bearer <token>, podemos omitir credentials aquí
// y evitar el conflicto sin perder seguridad.
export const svcUploadImage = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post('/uploads', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    withCredentials: false,   // ← evita conflicto con ACAO=* del ingress
    timeout: 120000,          // ← imágenes grandes: 2 min de gracia
  });
  return data;  // { url: '/api/uploads/xxx.webp', filename, size, ... }
};

// Rewrite any legacy absolute URLs that were pinned to an old preview host
// (or any Emergent preview/prod host) into a relative `/api/uploads/...`
// path. Non-upload URLs (unsplash, etc.) are returned untouched.

// Orders
export const svcListOrders = (params = {}) => api.get('/orders', { params }).then((r) => r.data);
export const svcCreateOrder = (body) => api.post('/orders', body).then((r) => r.data);
export const svcUpdateOrder = (id, patch) => api.patch(`/orders/${id}`, patch).then((r) => r.data);
export const svcDeleteOrder = (id) => api.delete(`/orders/${id}`).then((r) => r.data);

// Customers
export const svcListCustomers = () => api.get('/customers').then((r) => r.data);

// Loyalty
export const svcCreateLoyaltyCustomer = (body) => api.post('/loyalty/customers', body).then((r) => r.data);
export const svcSearchLoyaltyCustomers = (q = '') => api.get('/loyalty/customers', { params: { q } }).then((r) => r.data);
export const svcGetLoyaltyCustomer = (cedula) => api.get(`/loyalty/customers/${encodeURIComponent(cedula)}`).then((r) => r.data);
export const svcAddStamp = (body) => api.post('/loyalty/stamps', body).then((r) => r.data);
export const svcListGiftCards = (cedula = '') => api.get('/loyalty/gift-cards', { params: cedula ? { cedula } : {} }).then((r) => r.data);
export const svcValidateGiftCard = (code) => api.get(`/loyalty/gift-cards/${encodeURIComponent(code)}/validate`).then((r) => r.data);
// Abandoned carts
export const svcUpsertAbandonedCart = (body) => api.post('/cart/abandoned', body).then((r) => r.data);
export const svcListAbandonedCarts = () => api.get('/cart/abandoned').then((r) => r.data);
export const svcDismissAbandonedCart = (aid) => api.delete(`/cart/abandoned/${aid}`).then((r) => r.data);
export const svcMarkRecovered = (session_id) => api.post('/cart/abandoned/mark-recovered', { session_id }).then((r) => r.data);
export const svcRedeemGiftCard = (code, body = {}) => api.post(`/loyalty/gift-cards/${encodeURIComponent(code)}/redeem`, body).then((r) => r.data);

// Store variants
export const svcGetStoreVariants = (storeId) => api.get(`/stores/${storeId}/variants`).then((r) => r.data);
export const svcSetStoreVariants = (storeId, variant_availability) => api.patch(`/stores/${storeId}/variants`, { variant_availability }).then((r) => r.data);

// Gallery
export const svcListGallery = () => api.get('/gallery').then((r) => r.data);
export const svcCreateGallery = (body) => api.post('/gallery', body).then((r) => r.data);
export const svcUpdateGallery = (id, body) => api.patch(`/gallery/${id}`, body).then((r) => r.data);
export const svcDeleteGallery = (id) => api.delete(`/gallery/${id}`).then((r) => r.data);

// Articles (Educación Financiera)
export const svcListArticles = () => api.get('/articles').then((r) => r.data);
export const svcCreateArticle = (body) => api.post('/articles', body).then((r) => r.data);
export const svcUpdateArticle = (id, body) => api.patch(`/articles/${id}`, body).then((r) => r.data);
export const svcDeleteArticle = (id) => api.delete(`/articles/${id}`).then((r) => r.data);

// Testimonials
export const svcListTestimonials = () => api.get('/testimonials').then((r) => r.data);
export const svcCreateTestimonial = (body) => api.post('/testimonials', body).then((r) => r.data);
export const svcUpdateTestimonial = (id, body) => api.patch(`/testimonials/${id}`, body).then((r) => r.data);
export const svcDeleteTestimonial = (id) => api.delete(`/testimonials/${id}`).then((r) => r.data);
